import {
  getProductDetails,
  Product,
} from "../../../common/api/decimal/methods/product.ts";
import {
  json,
  LoaderFunction,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import {
  DecimalRequiredData,
  DecimalResult,
} from "../../../common/api/decimal/utils.ts";
import { loadBrandOnce } from "../../root/rootLoader.ts";
import { noRepeatCalls } from "../../../common/utils/function.ts";
import { isPrintProduct } from "./common/productUtils.ts";

type ProductTypeParam = "ebook" | "print" | "access-code" | "eresource";

const productFetcher = noRepeatCalls(getProductDetails);

export function getProductTypeParam(product: Product): ProductTypeParam {
  if (isPrintProduct(product)) {
    return "print";
  } else if (product.kind === "book") {
    return "ebook";
  } else if (product.kind === "Online Resource") {
    return "access-code";
  } else if (product.kind === "Online Resource Bundle") {
    return "eresource";
  }
  return "ebook";
}

export const productLoader: LoaderFunction = async ({ params, request }) => {
  const brand = await loadBrandOnce();
  if (!brand.PDP.enabled) {
    throw json({}, { status: 404 });
  }
  const url = new URL(request.url);
  const availableScopes: ProductTypeParam[] =
    (url.searchParams.get("scope")?.split(",") as ProductTypeParam[]) ?? [];

  const response = await productFetcher(params.vbid!);
  const filteredProducts =
    availableScopes.length > 0
      ? response.data?.filter((product) =>
          availableScopes.includes(getProductTypeParam(product))
        )
      : undefined;

  const productsToUse =
    filteredProducts && filteredProducts.length > 0
      ? filteredProducts
      : response.data!;

  return {
    ...response,
    data: productsToUse,
  };
};

function useProductLoaderData() {
  return useLoaderData() as DecimalRequiredData<DecimalResult<Product[]>>;
}

export function useActiveProduct() {
  const [params] = useSearchParams();
  const response = useProductLoaderData();

  const isbn = params.get("isbn");
  if (isbn) {
    const matchedProduct = response.data.find(
      (product) => product.canonicalIsbn === isbn
    );
    if (matchedProduct) {
      return matchedProduct;
    }
  }
  const type = params.get("asset");
  if (type) {
    const matchedProduct = response.data.find(
      (product) => getProductTypeParam(product) === type
    );
    if (matchedProduct) {
      return matchedProduct;
    }
  }

  return response.data[0];
}

export function useFilteredProducts() {
  return useProductLoaderData().data;
}
